import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import logoutHandler from '../utils/controllers/logoutHandler';
import { AI_INSIGHTS_URL, API_URL } from '../config/Config';

// Create a base query with common settings
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint, extra, requestId }) => {
    // Include the access token in headers if available
    const publicEndpoints = ['getBrandingConfig'];
    if (publicEndpoints.includes(endpoint)) {
      return headers;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

// Global variable to store the ongoing refresh promise
let refreshTokenPromise = null;

// Create a base query with token refresh handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.data?.errorObject?.errorCode === 401) {
    const oldRefreshToken = localStorage.getItem('refreshToken');

    // Third-party login (or missing refresh token): cannot renew — send user to login
    if (!oldRefreshToken) {
      logoutHandler();
      return result;
    }

    // If there's already a refresh in progress, wait for it
    if (refreshTokenPromise) {
      try {
        await refreshTokenPromise;
        // After the refresh is complete, retry the original request
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        // If refresh failed, the promise will handle logout
        return result;
      }
    } else {
      // Start a new refresh process
      refreshTokenPromise = (async () => {
        try {
          const response = await baseQuery(
            `/User/renewToken?refreshToken=${oldRefreshToken}`,
            api,
            extraOptions
          );

          if (response?.data?.success) {
            const { accessToken, refreshToken } = response?.data?.data;
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            return true; // Success
          } else {
            // Handle token refresh failure by removing tokens and redirecting to login
            logoutHandler();
            throw new Error('Token refresh failed');
          }
        } finally {
          // Clear the promise when done (success or failure)
          refreshTokenPromise = null;
        }
      })();

      try {
        await refreshTokenPromise;
        // Retry the original request after successful refresh
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        // Token refresh failed, return the original error
        return result;
      }
    }
  }

  return result;
};

// Create the API using Redux Toolkit's createApi
export const bridgedApi = createApi({
  reducerPath: 'bridgedApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'userInfo',
    'dashboard',
    'team',
    'cta',
    'eng',
    'domains',
    'pages',
    'agents',
    'collectionsV1',
    'collectionsV2',
    'collectionChunks',
    'actionBucket',
    'aiAgent',
    'smartReply',
    'onboarding',
    'aiLab',
    'folders',
    'fineTuning',
    'branding',
    'abTest',
    'webhooks',
    'webhookLogs',
    'Integrations',
    'events',
    'personalizationEmail',
    'labels',
  ],
  endpoints: builder => ({
    // ---------- User ------------
    // User registration mutation
    signUp: builder.mutation({
      query: data => ({
        url: '/User/Register',
        method: 'POST',
        body: data,
      }),
    }),

    // User login mutation
    login: builder.mutation({
      query: data => ({
        url: '/User/Login',
        method: 'POST',
        body: data,
      }),
    }),

    getBrandingConfig: builder.query({
      query: subdomain => ({
        url: `/api/public/branding?subdomain=${subdomain}`,
        method: 'GET',
      }),
      // This endpoint doesn't need auth, so we'll handle it specially
      transformResponse: response => {
        // Transform API response to match your structure
        return {
          logoUrl: response.logoUrl || '',
          primaryColor: response.primaryColor || '',
          companyName: response.companyName || '',
          subDomain: response.subDomain || '',
          playBooksSubscribed: response.playBooksSubscribed || [],
          brandingEnabled: !!(response.logoUrl || response.primaryColor || response.companyName),
        };
      },
      providesTags: ['branding'],
    }),

    // Resend verification email code mutation
    resendVerifyEmailCode: builder.mutation({
      query: data => ({
        url: '/User/ResendVerificationCode',
        method: 'POST',
        body: data,
      }),
    }),

    // Verify user's email mutation
    verifyUserEmail: builder.mutation({
      query: data => ({
        url: '/User/VerifyEmail',
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot password verification mutation
    forgotPasswordVerification: builder.mutation({
      query: data => ({
        url: '/User/ForgotPassword',
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot password mutation
    forgotPassword: builder.mutation({
      query: data => ({
        url: '/User/VerifyForgotCode',
        method: 'POST',
        body: data,
      }),
    }),

    // Google authentication query
    googleAuthentication: builder.query({
      query: accessToken => `/User/GoogleSignin?accessToken=${accessToken}`,
    }),

    // Facebook authentication query
    facebookAuthentication: builder.query({
      query: accessToken => `/User/FacebookSignin?accessToken=${accessToken}`,
    }),

    // ---------- Dashboard ------------

    dashboardOverview: builder.mutation({
      query: data => ({
        url: '/dashboard/Overview',
        method: 'POST',
        body: data,
      }),
    }),

    refreshDashboard: builder.mutation({
      query: () => ({
        url: '/Dashboard/RefreshDashboard',
        method: 'POST',
      }),
    }),

    // ------------------  Onboarding --------------

    getAllTasks: builder.query({
      query: () => '/Checklists/FindAll',
      providesTags: ['onboarding'],
    }),

    updateTask: builder.mutation({
      query: data => ({
        url: `Tasks/Completion?_id=${data._id}&isCompleted=${data.isCompleted}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    getTaskComments: builder.query({
      query: _id => `TaskComments/FindAllByTaskId/?_id=${_id}`,
      providesTags: ['onboarding'],
    }),

    createTaskComment: builder.mutation({
      query: data => ({
        url: 'TaskComments',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    updateTaskComment: builder.mutation({
      query: data => ({
        url: `TaskComments?_id=${data?.commentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    deleteTaskComment: builder.mutation({
      query: _id => ({
        url: `TaskComments?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['onboarding'],
    }),

    // ------------------  User --------------

    userInfo: builder.query({
      query: () => '/User/Profile',
      providesTags: ['userInfo'],
    }),

    updateUserInfo: builder.mutation({
      query: data => ({
        url: '/User/Profile',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['userInfo'],
    }),

    deleteUserAccount: builder.mutation({
      query: () => ({
        url: '/User',
        method: 'DELETE',
      }),
    }),

    changeUserPassword: builder.mutation({
      query: data => ({
        url: '/User/ChangePassword',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['userInfo'],
    }),

    // ---------- CTAs ------------
    createCta: builder.mutation({
      query: data => ({
        url: '/Action',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['cta'],
    }),

    editCta: builder.mutation({
      query: data => ({
        url: `/Action`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['cta'],
    }),

    deleteCta: builder.mutation({
      query: _id => ({
        url: `/Action?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['cta'],
    }),

    duplicateCta: builder.mutation({
      query: _id => ({
        url: `/Action/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['cta'],
    }),

    getAllUserCtas: builder.query({
      query: () => '/Action/UserAction',
      providesTags: ['cta'],
    }),

    getCtaById: builder.query({
      query: _id => `Action/FindById?_id=${_id}`,
      providesTags: ['cta'],
    }),

    // ---------- Engagements ------------

    createEng: builder.mutation({
      query: data => ({
        url: '/Engagement',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    editEng: builder.mutation({
      query: data => ({
        url: '/Engagement?constructionType=User_Generated',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    deleteEng: builder.mutation({
      query: _id => ({
        url: `/Engagement?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['eng'],
    }),

    duplicateEng: builder.mutation({
      query: _id => ({
        url: `/Engagement/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['eng'],
    }),

    getAllUserEng: builder.query({
      query: () => 'Engagement/UserEngagement',
      providesTags: ['eng'],
    }),

    getEngById: builder.query({
      query: _id => `Engagement/findWithId?_id=${_id}`,
      providesTags: ['eng'],
    }),

    // ---------- AI Engagement ------------
    getAllAIEng: builder.query({
      query: ({ pageNo, approvalState, search }) =>
        `EngagementsApprovalQueue/FindAll?approvalState=${approvalState}&pageNumber=${pageNo}&search=${search}`,
      providesTags: ['eng'],
    }),

    deleteAiEngagement: builder.mutation({
      query: data => ({
        url: `/Engagement/DeleteEngagementWithCampaignPageId`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    approveAIEng: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Approve?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    unApproveAIEng: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Reject?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    regenerateAIEngagement: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Regenerate?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    editAIEng: builder.mutation({
      query: data => ({
        url: `/Engagement`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    // ---------- End Screen ------------
    createEndScreen: builder.mutation({
      query: data => ({
        url: '/EndScreen',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['endScreen'],
    }),

    editEndScreen: builder.mutation({
      query: data => ({
        url: '/EndScreen',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['endScreen'],
    }),

    deleteEndScreen: builder.mutation({
      query: _id => ({
        url: `/EndScreen?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['endScreen'],
    }),

    duplicateEndScreen: builder.mutation({
      query: _id => ({
        url: `/EndScreen/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['endScreen'],
    }),

    getAllUserEndScreen: builder.query({
      query: () => 'EndScreen/FindAllCreatedUser',
      providesTags: ['endScreen'],
    }),

    getEndScreenById: builder.query({
      query: _id => `EndScreen/FindById?_id=${_id}`,
      providesTags: ['endScreen'],
    }),

    //  -------------- Collections V1 (actionBucket) ------------------
    createCollectionV1: builder.mutation({
      query: data => ({
        url: '/actionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getAllUserCollections: builder.query({
      query: () => '/actionBucket',
      providesTags: ['collectionsV1'],
    }),

    deleteCollectionV1: builder.mutation({
      query: _id => ({
        url: `/actionBucket?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    deleteCollection: builder.mutation({
      query: collectionId => ({
        url: `/collections?collectionId=${collectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    getCollectionOverview: builder.query({
      query: _id => `/actionBucket/overview?actionBucketId=${_id}`,
      providesTags: ['collectionsV1'],
    }),

    updateAuthInfo: builder.mutation({
      query: data => ({
        url: '/actionBucket/updateAuthInfo',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    renameCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/RenameActionBucket',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    refreshCtaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/RefreshActionFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    deleteCtaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/DeleteActionFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    updateUseAsReference: builder.mutation({
      query: data => ({
        url: 'actionBucket/actionBucketPageData/UpdateUseAsReference',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    deleteMediaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/DeleteMediaFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    updateMediaUseAsReference: builder.mutation({
      query: data => ({
        url: 'actionBucket/actionBucketMediaData/UpdateUseAsReference',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    // Update media reference URL for an existing media file in Action Bucket
    updateMediaPreferredLink: builder.mutation({
      query: body => ({
        url: '/ActionBucket/actionBucketMediaData/preferredLinkAndName',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    addMediaToCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/AddMediaToActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    addPageToCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/AddPageToActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getWordpressPluginConnection: builder.query({
      query: id => `/WordpressPlugin/FindByDomainId?domainId=${id}`,
      providesTags: ['collectionsV1'],
    }),

    getAllSitemaps: builder.mutation({
      query: data => ({
        url: '/Domain/FetchSitemaps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    refreshSitemap: builder.mutation({
      query: data => ({
        url: '/Domain/RefreshSitemaps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getXmlPageCrawlerStatus: builder.query({
      query: id => `/PageCrawler/xmlPageCrawlerStatus?domainId=${id}`,
      providesTags: ['collectionsV1'],
    }),

    startXMLCrawling: builder.mutation({
      query: data => ({
        url: '/PageCrawler/setupCrawler',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    addPageManuallyWithFile: builder.mutation({
      query: body => ({
        url: '/Page/addPageManuallyWithFile',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getTargetPageList: builder.mutation({
      query: data => ({
        url: '/Page/getPagesByDomainId',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getWordpressFilters: builder.query({
      query: domainId => `/Page/getAvailablePageFilterValues?domainId=${domainId}`,
      providesTags: ['collectionsV1'],
    }),

    //  -------------- New Collection ------------------
    createCollection: builder.mutation({
      query: data => ({
        url: '/collections',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    listCollections: builder.query({
      query: (params = {}) => ({
        url: '/collections',
        method: 'GET',
        params,
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        const data = response?.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const collections = Array.isArray(data.collections) ? data.collections : [];
          return {
            collections,
            total: data.total ?? collections.length,
          };
        }

        if (Array.isArray(data)) {
          return { collections: data, total: data.length };
        }

        if (Array.isArray(response?.collections)) {
          return {
            collections: response.collections,
            total: response.total ?? response.collections.length,
          };
        }

        return { collections: [], total: 0 };
      },
    }),

    getCollectionById: builder.query({
      query: _id => ({
        url: '/collections/GetById',
        method: 'GET',
        params: { _id },
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        const collection = response?.data;
        if (collection && typeof collection === 'object' && !Array.isArray(collection)) {
          return collection;
        }
        if (response?._id) {
          return response;
        }
        return {};
      },
    }),

    addCollectionItems: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections/items?collectionId=${collectionId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    addCollectionEntryPointItems: builder.mutation({
      query: ({ entryPointId, ...body }) => ({
        url: `/collections/entry-points/items?entryPointId=${entryPointId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    startCollectionIndexing: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections/indexing/start?collectionId=${collectionId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    deleteCollectionItem: builder.mutation({
      query: ({ collectionId, itemId }) => ({
        url: `/collections/items`,
        method: 'DELETE',
        body: { collectionId, itemId },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    deleteCollectionEntryPoint: builder.mutation({
      query: ({ collectionId, entryPointId }) => ({
        url: `/collections/entry-points`,
        method: 'DELETE',
        body: { collectionId, entryPointId },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    refreshCollection: builder.mutation({
      query: collectionId => ({
        url: `/collections/refresh?collectionId=${collectionId}`,
        method: 'POST',
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    getCollectionItems: builder.query({
      query: ({ collectionId, ...params }) => ({
        url: `/collections/items?collectionId=${collectionId}`,
        method: 'GET',
        params,
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        if (Array.isArray(response)) {
          return { items: response, entrypoints: [] };
        }

        const data = response?.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          return {
            items: Array.isArray(data.items) ? data.items : [],
            entrypoints: Array.isArray(data.entrypoints)
              ? data.entrypoints
              : Array.isArray(data.entryPoints)
                ? data.entryPoints
                : [],
            total: data.total,
          };
        }

        if (Array.isArray(data)) {
          return { items: data, entrypoints: [] };
        }

        if (Array.isArray(response?.items) || Array.isArray(response?.entrypoints)) {
          return {
            items: Array.isArray(response.items) ? response.items : [],
            entrypoints: Array.isArray(response.entrypoints)
              ? response.entrypoints
              : Array.isArray(response.entryPoints)
                ? response.entryPoints
                : [],
            total: response.total,
          };
        }

        return { items: [], entrypoints: [] };
      },
    }),

    getCollectionsModuleOverview: builder.query({
      query: collectionId => ({
        url: `/collections/overview?collectionId=${collectionId}`,
        method: 'GET',
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => response?.data || response || {},
    }),

    startSitemapFetch: builder.mutation({
      query: ({ collectionId, entryPointId, limit = 1500 }) => ({
        url: `/collections/entry-points/sitemap/fetch`,
        method: 'POST',
        body: { collectionId, entryPointId, limit },
      }),
    }),

    getSitemapFetchStatus: builder.query({
      query: ({ collectionId, entryPointId }) => ({
        url: `/collections/entry-points/sitemap/fetch-status?collectionId=${collectionId}&entryPointId=${entryPointId}`,
        method: 'GET',
      }),
      transformResponse: response => response?.data || response || {},
    }),

    editCollection: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections?collectionId=${collectionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    editCollectionItem: builder.mutation({
      query: ({ collectionId, itemId, ...body }) => ({
        url: `/collections/items`,
        method: 'PATCH',
        body: { collectionId, itemId, ...body },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    // ---------- Labels (the team's own Knowledge Base labels, plus Bridged's defaults) ----------

    getLabels: builder.query({
      query: () => ({
        url: '/labels',
        method: 'GET',
      }),
      providesTags: ['labels'],
      transformResponse: response => (Array.isArray(response?.data) ? response.data : []),
    }),

    createLabel: builder.mutation({
      query: body => ({
        url: '/labels',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['labels'],
    }),

    updateLabel: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/labels/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['labels', 'collectionsV2'],
    }),

    // Removes the label from every item and every agent's recommendation types.
    deleteLabel: builder.mutation({
      query: id => ({
        url: `/labels/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['labels', 'collectionsV2', 'agents'],
    }),

    getCollectionItemChunks: builder.query({
      query: ({ itemId, page = 0, limit = 20 }) => ({
        url: '/collections/items/chunks',
        method: 'GET',
        params: { itemId, page, limit },
      }),
      providesTags: ['collectionChunks'],
      transformResponse: response => response?.data || { chunks: [], total: 0 },
    }),

    // Pre-signed S3 links are fetched directly: our Authorization header would break the signature.
    // The cache is keyed on the file paths, so re-signed links refetch in place instead of
    // starting an empty entry (which would flash the loading state on every chunk poll).
    getKnowledgeBaseChunkFiles: builder.query({
      // A skipped query passes `skipToken` instead of the list of links.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${
          Array.isArray(queryArgs) ? queryArgs.map(url => url.split('?')[0]).join('|') : ''
        })`,
      forceRefetch: ({ currentArg, previousArg }) =>
        Array.isArray(currentArg) &&
        currentArg.join('|') !== (Array.isArray(previousArg) ? previousArg.join('|') : ''),
      queryFn: async urls => {
        try {
          const files = await Promise.all(
            urls.map(async url => {
              const response = await fetch(url);
              if (!response.ok) throw new Error(`Chunk file request failed (${response.status})`);
              return response.json();
            })
          );
          return { data: files.flatMap(file => file?.data || []) };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),

    editCollectionItemChunk: builder.mutation({
      query: body => ({
        url: '/collections/items/chunk',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['collectionChunks'],
    }),

    deleteCollectionItemChunk: builder.mutation({
      query: ({ itemId, chunkId }) => ({
        url: '/collections/items/chunk',
        method: 'DELETE',
        body: { itemId, chunkId },
      }),
      invalidatesTags: ['collectionChunks'],
    }),

    //  -------------- Team ------------------
    createTeam: builder.mutation({
      query: () => ({
        url: '/team',
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    getTeams: builder.query({
      query: () => '/team',
      providesTags: ['team'],
    }),

    getTeamById: builder.query({
      query: _id => `/team/teamMember?teamId=${_id}`,
      providesTags: ['team'],
    }),

    inviteTeamMember: builder.mutation({
      query: body => ({
        url: '/team/teamMember',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['team'],
    }),

    updateTeamMember: builder.mutation({
      query: body => ({
        url: '/team/teamMember',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['team'],
    }),

    deleteTeamMember: builder.mutation({
      query: _id => ({
        url: `/team/teamMember?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['team'],
    }),

    getPendingTeamInvites: builder.query({
      query: () => '/team/Invites',
      providesTags: ['team'],
    }),

    acceptTeamInvite: builder.mutation({
      query: _id => ({
        url: `/team/AcceptTeamInvitation?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    rejectTeamInvite: builder.mutation({
      query: _id => ({
        url: `/team/RejectTeamInvitation?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    getTeamAccessableSections: builder.query({
      query: () => '/team/accessableSections',
      providesTags: ['team'],
    }),

    // ---------- Domains ------------

    getAllDomains: builder.query({
      query: () => '/Domain/FindAll',
      providesTags: ['domain'],
    }),

    createDomain: builder.mutation({
      query: body => ({
        url: '/Domain',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['domain'],
    }),

    verifyDomain: builder.mutation({
      query: id => ({
        url: `/Domain/Verify?_id=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['domain'],
    }),

    deleteDomain: builder.mutation({
      query: _id => ({
        url: `/Domain?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['domain'],
    }),

    updatePageMetaData: builder.mutation({
      query: body => ({
        url: '/Domain/UpdatePageMetaDataConfigs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['domain'],
    }),

    // ---------- Pages ------------

    getAllPages: builder.mutation({
      query: body => ({
        url: '/Page/FindPages',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['pages'],
    }),

    getSinglePageData: builder.query({
      query: _id => `/Page/FindPageByIdWithCampaignInfo?pageId=${_id}`,
      providesTags: ['pages'],
    }),

    excludePageFromCampaign: builder.mutation({
      query: body => ({
        url: `/campaign/ExcludePageFromCampaign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['pages'],
    }),

    // ---------- Analytics ------------

    conversionRateChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `${AI_INSIGHTS_URL}/Performance/ConversionRateChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    engagementRateChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `${AI_INSIGHTS_URL}/Performance/EngagementRateChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    viewsChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `${AI_INSIGHTS_URL}/Performance/ViewsChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    timeSpent: builder.mutation({
      query: ({ filters, type }) => ({
        url: `${AI_INSIGHTS_URL}/Performance/TimeSpent?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    topActionCards: builder.mutation({
      query: body => ({
        url: `${AI_INSIGHTS_URL}/Performance/TopActionCards`,
        method: 'POST',
        body,
      }),
    }),

    topEngagementCards: builder.mutation({
      query: body => ({
        url: `${AI_INSIGHTS_URL}/Performance/TopEngagementCards`,
        method: 'POST',
        body,
      }),
    }),

    kaReferenceOverview: builder.mutation({
      query: body => ({
        url: `${AI_INSIGHTS_URL}/Performance/KaReferenceOverview`,
        method: 'POST',
        body,
      }),
    }),

    topEndScreenCards: builder.mutation({
      query: body => ({
        url: `${AI_INSIGHTS_URL}/Performance/TopEndScreenCards`,
        method: 'POST',
        body,
      }),
    }),

    timeLineChart: builder.mutation({
      query: body => ({
        url: `${AI_INSIGHTS_URL}/Performance/TimeLineChart`,
        method: 'POST',
        body,
      }),
    }),

    topPerformingPages: builder.mutation({
      query: ({ filters, type }) => ({
        url: `/Analytics/Performance/TopPerformingPages?filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    performanceChart: builder.mutation({
      query: filters => ({
        url: `${AI_INSIGHTS_URL}/Users/PerformanceChart`,
        method: 'POST',
        body: filters,
      }),
    }),

    countriesChart: builder.mutation({
      query: filters => ({
        url: `${AI_INSIGHTS_URL}/Users/Countries`,
        method: 'POST',
        body: filters,
      }),
    }),

    citiesChart: builder.mutation({
      query: filters => ({
        url: `${AI_INSIGHTS_URL}/Users/Cities`,
        method: 'POST',
        body: filters,
      }),
    }),

    browsersChart: builder.mutation({
      query: filters => ({
        url: `${AI_INSIGHTS_URL}/Users/Browsers`,
        method: 'POST',
        body: filters,
      }),
    }),

    devicesChart: builder.mutation({
      query: filters => ({
        url: `${AI_INSIGHTS_URL}/Users/Devices`,
        method: 'POST',
        body: filters,
      }),
    }),

    // ---------- Agents ------------

    userConfiguration: builder.query({
      query: () => '/UserConfiguration',
      providesTags: ['agents'],
    }),

    allAgents: builder.query({
      query: () => '/Campaign/FindAllUserCampaigns',
      providesTags: ['agents'],
    }),

    getAgentById: builder.query({
      query: id => `/Campaign/FindCampaignByIdForUpdate?campaignId=${id}`,
      providesTags: ['agents'],
    }),

    createAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    updateAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/UpdateCampaign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    deleteAgent: builder.mutation({
      query: _id => ({
        url: `/Campaign/?campaignId=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['agents'],
    }),

    editAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/UpdateCampaign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    duplicateAgent: builder.mutation({
      query: id => ({
        url: `campaign/duplicate?campaignId=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['agents'],
    }),

    editAgentState: builder.mutation({
      query: id => ({
        url: `/Campaign/ToggleState?campaignId=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['agents'],
    }),

    createScreenFromTemplate: builder.mutation({
      query: body => ({
        url: '/Template',
        method: 'POST',
        body,
      }),
    }),

    getAgentOverviewCacheStatus: builder.mutation({
      query: body => ({
        url: '/Campaign/GetOverviewCacheStatus',
        method: 'POST',
        body,
      }),
    }),

    refreshAgentOverview: builder.mutation({
      query: body => ({
        url: '/Campaign/RefreshOverview',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewSummary: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/Summary',
        method: 'POST',
        body,
      }),
    }),

    downloadPersonalizationToolRegistrationLeads: builder.mutation({
      query: body => ({
        url: '/campaign/personalizationTool/registerationLeads',
        method: 'POST',
        body,
      }),
    }),

    agentActivity: builder.mutation({
      query: body => ({
        url: '/activity/campaignActivity',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTimeline: builder.mutation({
      query: body => ({
        url: '/Campaign/OverviewTimeline',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagements: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements',
        method: 'POST',
        body,
      }),
    }),
    whatsAppOverviewTopEngagements: builder.mutation({
      query: body => ({
        url: '/SmartReply/Overview/TopEngagements/Pagination',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagementsPagination: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements/Pagination',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagementsDownload: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements/Download',
        method: 'POST',
        body,
      }),
    }),

    getKAReferenceOverview: builder.mutation({
      query: body => ({
        url: '/Campaign/GetKaReferenceOverview',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopActions: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopActions',
        method: 'POST',
        body,
      }),
    }),

    agentIRISSessions: builder.mutation({
      query: body => ({
        url: '/Campaign/GetIRISSessions',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewWordCloud: builder.mutation({
      query: body => ({
        url: '/Campaign/GetWordCloud',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewWordCloudDownload: builder.mutation({
      query: body => ({
        url: '/Campaign/DownloadRagResult',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewFeedback: builder.mutation({
      query: body => ({
        url: '/Campaign/GetCampaignOverviewFeedback',
        method: 'POST',
        body,
      }),
    }),

    downloadAgentOverviewFeedback: builder.mutation({
      query: body => ({
        url: '/Campaign/DonwloadCampaignOverviewFeedback',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewPages: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/Pages',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewPagesDownload: builder.mutation({
      query: id => ({
        url: `/Campaign/Overview/Pages/download/?campaignId=${id}`,
        method: 'POST',
      }),
    }),

    agentOverviewTopVisitors: builder.query({
      query: ({ campaignId, pageNumber, startDate, endDate, limit }) =>
        `Campaign/Overview/Visitors?campaignId=${campaignId}&pageNumber=${pageNumber}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
      invalidatesTags: ['agents'],
    }),

    agentOverviewTopVisitorsDynamicForm: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/actionTakerDynamicTable',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopVisitorsJourneyCtas: builder.query({
      query: campaignId => ({
        url: `/Campaign/getCampaignActions?campaignId=${campaignId}`,
        method: 'GET',
      }),
    }),

    uploadFont: builder.mutation({
      query: body => ({
        url: '/Media/font',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['fonts'],
    }),

    getAllFontHistory: builder.query({
      query: () => '/Media/FontsHistory',
      providesTags: ['fonts'],
    }),

    agentOverviewTopVisitorsDownload: builder.mutation({
      query: body => ({
        // url: '/Campaign/Overview/Visitors/Download/',
        url: '/Campaign/Overview/actionTakerDynamicTable/Download',
        method: 'POST',
        body,
      }),
    }),

    AIGeneratedExamples: builder.mutation({
      query: body => ({
        url: '/Engagement/AIGeneratedExample',
        method: 'POST',
        body,
      }),
    }),

    getDomainConfigHistory: builder.query({
      query: _id => `/Campaign/GetDomainConfigHistory`,
      providesTags: ['campaign'],
    }),

    clearFAQCache: builder.mutation({
      query: body => ({
        url: '/campaign/ClearFAQCache',
        method: 'POST',
        body,
      }),
    }),

    // ---------------------- AI Brand Sync ----------------------
    domainBrandSync: builder.query({
      query: domainId => ({
        url: `/Domain/BrandSync?domainId=${domainId}`,
        method: 'GET',
      }),
    }),

    getAIBrandSyncSuggestions: builder.mutation({
      query: body => ({
        url: '/Domain/BrandSync/Suggest',
        method: 'POST',
        body,
      }),
    }),

    // ---------------------- AI Agent ----------------------

    AllAIAgents: builder.query({
      query: () => '/aiAgent/GetUserAIAGents',
      providesTags: ['aiAgent', 'agents'],
    }),

    getAIAgentById: builder.query({
      query: id => `/aiAgent/GetUserAIAGentById?id=${id}`,
      providesTags: ['aiAgent', 'agents'],
    }),

    createAIAgent: builder.mutation({
      query: body => ({
        url: '/aiAgent/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    updateAIAgent: builder.mutation({
      query: body => ({
        url: '/aiAgent/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    deleteAIAgent: builder.mutation({
      query: _id => ({
        url: `/aiAgent/Delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    // ---------------------- Smart Reply ----------------------

    getAllSmartReplies: builder.query({
      query: () => '/SmartReply/Agents/',
      providesTags: ['smartReply'],
    }),

    getSmartReplyById: builder.query({
      query: _id => `/SmartReply/Agent/?_id=${_id}`,
      providesTags: ['smartReply'],
    }),

    createSmartReply: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    updateSmartReply: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/SmartReply/Agent/?_id=${_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    deleteSmartReply: builder.mutation({
      query: _id => ({
        url: `/SmartReply/Agent/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    // ---------------------- Smart Reply Overview ----------------------

    getSmartReplyOverview: builder.query({
      query: ({ _id, startDate, endDate }) =>
        `/SmartReply/Agent/Overview/?_id=${_id}&startDate=${startDate}&endDate=${endDate}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyOverviewTimeline: builder.query({
      query: ({ _id, startDate, endDate }) =>
        `/SmartReply/Agent/Timeline?_id=${_id}&startDate=${startDate}&endDate=${endDate}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyEmailLogs: builder.query({
      query: _id => `/SmartReply/Agent/Emails?agentId=${_id}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyEmailThread: builder.query({
      query: _id => `/SmartReply/Agent/Email?_id=${_id}`,
      providesTags: ['smartReply'],
    }),

    // ---------------------- Smart Reply Whatsapp Overview ----------------------
    getSmartReplyWhatsappOverview: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/Overview',
        method: 'POST',
        body,
      }),
      providesTags: ['smartReply'],
    }),
    setSmartReplyBroadcast: builder.mutation({
      query: body => ({
        url: '/smartReply/schedules',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        { type: 'smartReply', id: body.smartReplyAgentId },
      ],
    }),
    getSmartReplyBroadcastList: builder.query({
      query: id => ({
        url: `/smartReply/schedules?smartReplyAgentId=${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'smartReply', id }],
    }),
    deleteSmartReplyBroadcast: builder.mutation({
      query: id => ({
        url: `/smartReply/schedules?id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['smartReply'],
    }),

    // ---------------------- Personalization Email Channel ----------------------
    getPtTargetList: builder.query({
      query: ({ agentId, pageNumber, limit, ...filters }) => ({
        url: `/targetList/search?agentId=${agentId}`,
        method: 'POST',
        body: { pageNumber, limit, ...filters },
      }),
      providesTags: ['personalizationEmail'],
    }),

    getPtTargetListMetaDataFields: builder.query({
      query: ({ agentId }) => `/targetList/metadata?agentId=${agentId}`,
      providesTags: ['personalizationEmail'],
    }),

    importProfilesFromCsv: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/profile/import/csv',
        method: 'POST',
        body,
      }),
    }),

    getCsvImportColumns: builder.query({
      query: mediaId => `/PersonalizationTool/profile/import/csv/columns?mediaId=${mediaId}`,
    }),

    importProfilesFromHubspot: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/profile/import/hubspot',
        method: 'POST',
        body,
      }),
    }),

    getProfileImportStatus: builder.query({
      query: id => ({
        url: `/PersonalizationTool/profile/import/status/?importId=${id}`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: 'personalizationEmail', id }],
    }),

    getPersonalizationProfileList: builder.query({
      query: body => ({
        url: '/personalizationTool/profile/list',
        method: 'POST',
        body,
      }),
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `profiles-${args?.importId}` },
      ],
    }),

    getCrmIntegrationsList: builder.query({
      query: () => ({
        url: '/integration?type=hubspot',
        method: 'GET',
      }),
      providesTags: ['personalizationEmail'],
    }),

    getIntegrationHubProperties: builder.query({
      query: integrationId => `/integration/hubspot/properties?integrationId=${integrationId}`,
      providesTags: (result, error, integrationId) => [
        { type: 'personalizationEmail', id: `properties-${integrationId}` },
      ],
    }),

    createHubspotProperty: builder.mutation({
      query: body => ({
        url: '/integration/hubspot/properties',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        { type: 'personalizationEmail', id: `properties-${body?.integrationId}` },
      ],
    }),

    connectEmailChannelHubspot: builder.mutation({
      query: body => ({
        url: '/Integration',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    deleteCrmIntegration: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    verifyHubspotIntegration: builder.mutation({
      query: id => ({
        url: `/integration/hubspot/verify?integrationId=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgents: builder.query({
      query: () => '/PersonalizationTool/Agent/List',
      providesTags: ['personalizationEmail'],
    }),

    createPersonalizationAgent: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/Agent',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    previewPersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent/Preview?agentId=${agentId}`,
        method: 'POST',
      }),
    }),

    startPersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent/Start?agentId=${agentId}`,
        method: 'POST',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgent: builder.query({
      query: agentId => `/PersonalizationTool/Agent/overview?agentId=${agentId}`,
      providesTags: (result, error, agentId) => [{ type: 'personalizationEmail', id: agentId }],
    }),

    deletePersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent?agentId=${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgentProfiles: builder.query({
      query: body => ({
        url: '/PersonalizationTool/Agent/overview/Profiles',
        method: 'POST',
        body,
      }),
      providesTags: (result, error, args) => [{ type: 'personalizationEmail', id: args?.agentId }],
    }),

    getPersonalizationAgentProfile: builder.query({
      query: ({ agentId, profileId }) =>
        `/PersonalizationTool/Agent/Profile?agentId=${agentId}&profileId=${profileId}`,
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.profileId },
      ],
    }),

    retryPersonalizationAgentFailedProfiles: builder.mutation({
      query: ({ agentId, profileIds }) => ({
        url: '/PersonalizationTool/Agent/RetryFailed',
        method: 'POST',
        body: { agentId, profileIds },
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.agentId },
      ],
    }),

    downloadPersonalizationAgentExport: builder.mutation({
      query: agentId => `/PersonalizationTool/Agent/Download?agentId=${agentId}`,
    }),

    publishPersonalizationAgentIntegration: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/Agent/PublishIntegration/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.agentId },
        { type: 'personalizationEmail', id: `deliveries-${args?.agentId}` },
        'personalizationEmail',
      ],
    }),

    getPersonalizationAgentPublishStatus: builder.query({
      query: publishId =>
        `/PersonalizationTool/Agent/PublishIntegration/Status?publishId=${publishId}`,
    }),

    getPersonalizationAgentDeliveries: builder.query({
      query: ({ agentId, page, pageSize }) =>
        `/PersonalizationTool/Agent/Deliveries?agentId=${agentId}&page=${page}&pageSize=${pageSize}`,
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `deliveries-${args?.agentId}` },
        'personalizationEmail',
      ],
    }),

    getPTInputLogs: builder.query({
      query: ({ agentId, pageNumber, limit, startDate, endDate, hasRegistration }) => {
        const params = new URLSearchParams({ agentId });
        if (pageNumber != null) params.set('pageNumber', String(pageNumber));
        if (limit != null) params.set('limit', String(limit));
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (hasRegistration !== undefined) {
          params.set('hasRegistration', hasRegistration ? 'true' : 'false');
        }
        return `/Campaign/personalizationToolOverview/inputLogs?${params}`;
      },
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `pt-input-logs-${args?.agentId}` },
      ],
    }),

    getPersonalizationById: builder.query({
      query: id => `/Campaign/personalizationById?id=${id}`,
      providesTags: (result, error, id) => [
        { type: 'personalizationEmail', id: `pt-journey-${id}` },
      ],
    }),

    getPTTopChunks: builder.query({
      query: ({ agentId, chunksPerLabel = 5, startDate, endDate }) => {
        const params = new URLSearchParams({
          agentId,
          chunksPerLabel: String(chunksPerLabel),
        });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/topChunks?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    getPTFunnelOverview: builder.query({
      query: ({ agentId, startDate, endDate }) => {
        const params = new URLSearchParams({ agentId });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/funnelOverview?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    getPTRecommendationTypes: builder.query({
      query: ({ agentId, startDate, endDate }) => {
        const params = new URLSearchParams({ agentId });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/recommendationTypes?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    // ---------------------- Qualification ----------------------

    getAllQualificationAgents: builder.query({
      query: () => '/QualificationAgent/FindAll',
      providesTags: ['qualification'],
    }),

    createQualification: builder.mutation({
      query: body => ({
        url: '/QualificationAgent/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    getQualificationById: builder.query({
      query: _id => `/QualificationAgent/?_id=${_id}`,
      providesTags: ['qualification'],
    }),

    updateQualification: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/QualificationAgent/?_id=${_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    deleteQualification: builder.mutation({
      query: _id => ({
        url: `/QualificationAgent/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    // ---------------------- Qualificaiton agent v1 ----------------

    getAllQualificationAgentsV1: builder.query({
      query: () => '/QualificationAgent/v1/FindAll',
      providesTags: ['qualification', 'agents'],
    }),

    createQualificationAgentV1: builder.mutation({
      query: body => ({
        url: '/QualificationAgent/v1/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    updateQualificationAgentV1: builder.mutation({
      query: ({ agentId, body }) => ({
        url: `/QualificationAgent/v1/?agentId=${agentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    getQualificationAgentV1ById: builder.query({
      query: agentId => `/QualificationAgent/v1/?agentId=${agentId}`,
      providesTags: ['qualification', 'agents'],
    }),

    deleteQualificationAgentV1: builder.mutation({
      query: agentId => ({
        url: `/QualificationAgent/v1/?agentId=${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    // ---------------------- Qualification Overview ----------------------
    getQualificationOverview: builder.query({
      query: _id => `/QualificationAgent/Overview/?_id=${_id}`,
      providesTags: ['qualification'],
    }),

    getQualificationAudience: builder.query({
      query: ({ _id, stageType, pageNumber, withEmailOnly }) =>
        `/QualificationAgent/Audiences/?_id=${_id}&stageType=${stageType}&pageNumber=${pageNumber}&withEmailOnly=${withEmailOnly}`,
      providesTags: ['qualification'],
    }),

    getQualificationStageBreakdown: builder.query({
      query: agentId => `/QualificationAgent/StagesBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    getQualificationSignalsBreakdown: builder.query({
      query: agentId => `/QualificationAgent/SignalsBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    getQualificationVisitorsBreakdown: builder.query({
      query: agentId => `/QualificationAgent/VisitorsBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    // Smart Reply Feedback
    submitSmartReplyFeedback: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/Feedback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['smartReply'],
    }),

    // ---------------------- Folders ----------------------

    getAllFolders: builder.query({
      query: ({ eventId, workflowId } = {}) => {
        const params = eventId && workflowId ? `?eventId=${eventId}&workflowId=${workflowId}` : '';
        return `/Folder/FetchByUserId${params}`;
      },
      providesTags: ['folders', 'agents'],
    }),

    getFolderById: builder.query({
      query: arg => {
        const { folderId, eventId, workflowId } =
          typeof arg === 'string' ? { folderId: arg } : arg || {};
        const params = new URLSearchParams({ folderId });
        if (eventId && workflowId) {
          params.set('eventId', eventId);
          params.set('workflowId', workflowId);
        }
        return `/Folder/FetchById?${params.toString()}`;
      },
      providesTags: ['folders', 'agents'],
    }),
    addCampaignToFolder: builder.mutation({
      query: body => ({
        url: '/Folder/Addfolderitem',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['folders'],
    }),
    removeCampaignFromFolder: builder.mutation({
      query: body => ({
        url: '/Folder/Removefolderitem',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['folders'],
    }),

    createFolder: builder.mutation({
      query: body => ({
        url: '/Folder',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['folders'],
    }),

    updateFolder: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/Folder?id=${_id}`,
        method: 'PUT',
        body: { _id, ...body },
      }),
      invalidatesTags: ['folders'],
    }),

    deleteFolder: builder.mutation({
      query: _id => ({
        url: `/Folder?id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['folders'],
    }),

    // ---------------------- Action Bucket ----------------------

    getAllActionBucket: builder.query({
      query: () => '/actionBucket',
      providesTags: ['actionBucket'],
    }),

    // ----------------------- Leads -----------------------

    getAllLeadsList: builder.mutation({
      query: body => ({
        url: '/Campaign/ContactsList',
        method: 'POST',
        body,
      }),
    }),

    // downloadLeadsList: builder.mutation({
    //   query: body => ({
    //     url: 'Submissions/download',
    //     method: 'POST',
    //     body,
    //   }),
    // }),

    // ----------------------- Profiles (Global profile management) -----------------------

    // getProfiles: builder.mutation({
    //   query: body => ({
    //     url: '/profile/table',
    //     method: 'POST',
    //     body,
    //   }),
    // }),

    getProfiles: builder.mutation({
      query: body => {
        const { eventId, ...requestBody } = body || {};
        const params = new URLSearchParams();
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();

        return {
          url: `profile/SubmissionsTable${query ? `?${query}` : ''}`,
          method: 'POST',
          body: requestBody,
        };
      },
    }),

    getTargetList: builder.mutation({
      query: ({ body, agentId }) => ({
        url: `/targetList/search?agentId=${agentId}`,
        method: 'POST',
        body,
      }),
    }),

    downloadTargetList: builder.mutation({
      query: ({ agentId, body }) => ({
        url: `/targetList/download?agentId=${encodeURIComponent(agentId)}`,
        method: 'POST',
        body,
      }),
    }),

    uploadTargetListCsv: builder.mutation({
      query: formData => ({
        url: '/media/upload/csv',
        method: 'POST',
        body: formData,
      }),
    }),

    importTargetList: builder.mutation({
      query: body => ({
        url: '/targetList/import',
        method: 'POST',
        body,
      }),
    }),

    getTargetListImportStatus: builder.query({
      query: targetListImportId => ({
        url: `/targetList/import/status?targetListImportId=${encodeURIComponent(targetListImportId)}`,
        method: 'GET',
      }),
    }),

    getTargetListImportDetails: builder.query({
      query: targetListImportId => ({
        url: `/targetList/import?targetListImportId=${encodeURIComponent(targetListImportId)}`,
        method: 'GET',
      }),
    }),

    getTargetListMetaDataFields: builder.query({
      query: ({ agentId }) => ({
        url: `/targetList/metadata?agentId=${agentId}`,
        method: 'GET',
      }),
    }),

    downloadProfiles: builder.mutation({
      query: body => {
        const { eventId, ...requestBody } = body || {};
        const params = new URLSearchParams();
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();

        return {
          url: `/profile/Submissions/download${query ? `?${query}` : ''}`,
          method: 'POST',
          body: requestBody,
        };
      },
    }),

    getProfilesMetaDataFields: builder.query({
      query: (arg = {}) => {
        const campaignId = typeof arg === 'string' ? arg : arg?.campaignId;
        const smartReplyAgentId =
          typeof arg === 'object' && arg !== null ? arg?.smartReplyAgentId : undefined;
        const eventId = typeof arg === 'object' && arg !== null ? arg?.eventId : undefined;
        const params = new URLSearchParams();
        if (campaignId) params.set('campaignId', campaignId);
        if (smartReplyAgentId) params.set('smartReplyAgentId', smartReplyAgentId);
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();
        return {
          url: `/profile/GetMetaDataFields${query ? `?${query}` : ''}`,
          method: 'GET',
        };
      },
    }),

    // ----------------------- Draft ---------------------

    getAllDrafts: builder.query({
      query: type => `/Draft/FindAllByDraftType?draftType=${type}`,
      providesTags: ['draft'],
    }),

    getSingleDraft: builder.query({
      query: _id => `/Draft/FindById?_id=${_id}`,
      providesTags: ['draft'],
    }),

    createDraft: builder.mutation({
      query: body => ({
        url: '/Draft/Create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['draft'],
    }),

    deleteDraft: builder.mutation({
      query: _id => ({
        url: `/Draft/Delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['draft'],
    }),

    // ----------------------- AI Lab -----------------------

    getAllEngines: builder.query({
      query: () => '/Engine/FindAll',
      providesTags: ['aiLab'],
    }),

    getDefaultEngine: builder.query({
      query: () => '/Engine/FindAll?isDefault=true',
      providesTags: ['aiLab'],
    }),

    getNoDefaultEngine: builder.query({
      query: () => '/Engine/FindAll?isDefault=false',
      providesTags: ['aiLab'],
    }),

    getEngineById: builder.query({
      query: _id => `/Engine/FindById?_id=${_id}`,
      providesTags: ['aiLab'],
    }),

    deleteEngine: builder.mutation({
      query: _id => ({
        url: `/Engine/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiLab'],
    }),

    createEngine: builder.mutation({
      query: body => ({
        url: '/Engine/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    updateEngine: builder.mutation({
      query: body => ({
        url: `/Engine?_id=${body?._id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),
    updateAndSelectEngine: builder.mutation({
      query: body => ({
        url: `/engine/version/editAndSelect`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    getEngineHistory: builder.query({
      query: _id => `/engine/versions?_id=${_id}`,
      providesTags: ['aiLab'],
    }),

    deleteEngineVersion: builder.mutation({
      query: _id => ({
        url: `/engine/version/delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiLab'],
    }),

    editEngineVersion: builder.mutation({
      query: body => ({
        url: `/engine/version/editTitle`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    duplicateEngine: builder.mutation({
      query: _id => ({
        url: `/Engine/Duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['aiLab'],
    }),

    saveEngineVersion: builder.mutation({
      query: body => ({
        url: '/engine/saveAsVersion',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    rollbackEngineVersion: builder.mutation({
      query: body => ({
        url: '/Engine/version/select',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),
    bulkSimulationHistory: builder.query({
      query: engineId => ({
        url: `/Engine/simulationFile/history?engineId=${engineId}`,
        method: 'GET',
      }),
    }),

    simulateEngine: builder.mutation({
      query: body => ({
        url: '/Engine/SimulateEngine',
        method: 'POST',
        body,
      }),
    }),

    simulateFAQCreations: builder.mutation({
      query: body => ({
        url: '/Engine/SimulateFAQCreations',
        method: 'POST',
        body,
      }),
    }),

    uploadSimulateEngineResult: builder.mutation({
      query: ({ engineId, actionBucketIds, body }) => {
        const ids = Array.isArray(actionBucketIds) ? actionBucketIds : [];
        const query = new URLSearchParams({ engineId: engineId || '' });
        ids.forEach(id => query.append('actionBucketIds', id));
        return {
          url: `/Engine/simulationFile/upload?${query.toString()}`,
          method: 'POST',
          body,
        };
      },
    }),

    startSimulationFile: builder.mutation({
      query: body => ({
        url: '/Engine/simulationFile/start',
        method: 'POST',
        body,
      }),
    }),

    getSimulateStatus: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/status?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    getSimulationResult: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/result?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    startLlmEval: builder.mutation({
      query: body => ({
        url: '/Engine/simulationFile/llmEval',
        method: 'POST',
        body,
      }),
    }),

    getLlmEvalStatus: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/llmEval/status?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    getLlmEvalResult: builder.mutation({
      query: evalId => ({
        url: `/Engine/simulationFile/llmEval/result?evalId=${evalId}`,
        method: 'GET',
      }),
    }),

    getLatestSimulate: builder.mutation({
      query: engineId => ({
        url: `/Engine/simulationFile/latest?engineId=${engineId}`,
        method: 'GET',
      }),
    }),

    generateGuideline: builder.mutation({
      query: body => ({
        url: '/Engine/GenerateGuideline',
        method: 'POST',
        body,
      }),
    }),

    checkSpamGuideline: builder.mutation({
      query: body => ({
        url: '/Engine/CheckEmailSpam',
        method: 'POST',
        body,
      }),
    }),

    createFineTuningJob: builder.mutation({
      query: body => ({
        url: 'engine/fineTuning',
        method: 'POST',
        body,
      }),
      transformResponse: response => {
        // The API answers { success, ... } (lowercase). Normalise to `Success` for the caller
        // instead of swapping in a mock, which turned a real `success: false` into "created".
        if (response && typeof response === 'object') {
          const success = response.Success ?? response.success;
          if (success !== undefined) return { ...response, Success: Boolean(success) };
        }
        // An empty 2xx body: the request went through.
        return { Success: true, data: response ?? null };
      },
      invalidatesTags: ['fineTuning'],
    }),

    getFineTuningJobs: builder.query({
      query: () => '/Engine/FineTuning',
      providesTags: ['fineTuning'],
    }),

    getEngineFeedbackPaginationWithFilter: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetByEngineInformation',
        method: 'POST',
        body: payload,
      }),
      providesTags: ['aiLab'],
    }),

    getFeedbackCountByType: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetFeedbackCountByType',
        method: 'POST',
        body: payload,
      }),
      providesTags: ['fineTuning'],
    }),

    getFeedbackCountByTypeTable: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetFeedbacks',
        method: 'POST',
        body: payload,
      }),
      //   // Mock data for table with pagination
      //   const { page = 0, limit = 15 } = payload || {};
      //   const allMockData = [
      //     {
      //       _id: '1',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Great response!',
      //       Source: 'Discuss with Sina',
      //       createdAt: new Date().toISOString(),
      //     },
      //     {
      //       _id: '2',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Not accurate enough',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-14T14:20:00Z',
      //     },
      //     {
      //       _id: '3',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Needs improvement in context understanding',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-13T09:15:00Z',
      //     },
      //     {
      //       _id: '4',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Excellent performance',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-12T16:45:00Z',
      //     },
      //     {
      //       _id: '5',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Response was too slow',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-11T11:30:00Z',
      //     },
      //     {
      //       _id: '6',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Very helpful',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-10T13:20:00Z',
      //     },
      //     {
      //       _id: '7',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Requires better formatting',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-09T08:10:00Z',
      //     },
      //     {
      //       _id: '8',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Perfect answer',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-08T15:25:00Z',
      //     },
      //     {
      //       _id: '9',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Incorrect information',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-07T10:40:00Z',
      //     },
      //     {
      //       _id: '10',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Good quality response',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-06T12:50:00Z',
      //     },
      //     {
      //       _id: '11',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Needs more detail',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-05T09:30:00Z',
      //     },
      //     {
      //       _id: '12',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Clear and concise',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-04T14:15:00Z',
      //     },
      //     {
      //       _id: '13',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Missing key points',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-03T11:20:00Z',
      //     },
      //     {
      //       _id: '14',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Very accurate',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-02T16:35:00Z',
      //     },
      //     {
      //       _id: '15',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Should include examples',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-01T10:00:00Z',
      //     },
      //   ];

      //   // Apply pagination
      //   const startIndex = page * limit;
      //   const endIndex = startIndex + limit;
      //   const paginatedData = allMockData.slice(startIndex, endIndex);

      //   return {
      //     data: {
      //       data: paginatedData,
      //       totalLength: allMockData.length,
      //     },
      //   };
      // },
      providesTags: ['fineTuning'],
    }),

    // ----------------------- Etc -----------------------
    uploadImage: builder.mutation({
      query: body => ({
        url: '/Media/image',
        method: 'POST',
        body,
      }),
    }),

    uploadDocument: builder.mutation({
      query: body => ({
        url: '/Media/document',
        method: 'POST',
        body,
      }),
    }),

    deleteImage: builder.mutation({
      query: body => ({
        url: '/Media',
        method: 'DELETE',
        body,
      }),
    }),

    Feedback: builder.mutation({
      query: data => ({
        url: '/Dashboard/Feedback',
        method: 'POST',
        body: data,
      }),
    }),

    Support: builder.mutation({
      query: data => ({
        url: '/Dashboard/Support',
        method: 'POST',
        body: data,
      }),
    }),

    // Credit Usage
    getCreditUsage: builder.query({
      query: () => '/credits/summary',
      providesTags: ['creditUsage'],
    }),

    getCreditUsageTimeline: builder.mutation({
      query: data => ({
        url: '/credits/timeline',
        method: 'POST',
        body: data,
      }),
    }),

    getTeamBrandingConfig: builder.query({
      query: () => '/team/brand',
      method: 'GET',
      // Branding can change outside this app; never keep a stale cache.
      keepUnusedDataFor: 0,
      providesTags: ['branding'],
    }),

    getAgentSummary: builder.mutation({
      query: data => ({
        url: '/credits/agentSummary',
        method: 'POST',
        body: data,
      }),
    }),

    getPlaybooks: builder.query({
      query: () => '/playbooks',
      method: 'GET',
    }),

    createBridgedEvent: builder.mutation({
      query: body => ({
        url: '/bridged-events',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    getBridgedEvents: builder.query({
      query: ({ page = 1, limit = 12 } = {}) => ({
        url: '/bridged-events',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['events'],
    }),

    getBridgedEventById: builder.query({
      query: id => ({
        url: '/bridged-events',
        method: 'GET',
        params: { id },
      }),
      providesTags: ['events'],
    }),

    updateBridgedEvent: builder.mutation({
      query: body => ({
        url: '/bridged-events',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    deleteBridgedEvent: builder.mutation({
      query: id => ({
        url: '/bridged-events',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['events'],
    }),
    refreshCreditUsage: builder.mutation({
      query: () => ({
        url: '/Credits/RefreshCreditUsage',
        method: 'POST',
      }),
    }),

    getCreditUsageCustomWorks: builder.mutation({
      query: () => ({
        url: '/Credits/CustomWork',
        method: 'GET',
      }),
    }),

    getSmartReplyTemplates: builder.query({
      query: agentId => ({
        url: `/SmartReply/Templates/?smartReplyAgentId=${agentId}`,
        method: 'GET',
      }),
    }),

    getLedgerHistory: builder.query({
      query: () => ({
        url: '/credits/TeamHistory',
        method: 'GET',
      }),
    }),
    //Sponsors
    getSponsorsList: builder.query({
      query: () => ({
        url: '/Sponsors',
        method: 'GET',
      }),
      invalidatesTags: ['sponsors'],
    }),

    getSponsorById: builder.query({
      query: ({ id }) => ({
        url: `/sponsors/detail?sponsorId=${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      invalidatesTags: ['sponsors'],
    }),

    createSponsor: builder.mutation({
      query: data => ({
        url: '/Sponsors',
        method: 'POST',
        body: data,
      }),
    }),

    updateSponsor: builder.mutation({
      query: ({ data, id }) => ({
        url: `/Sponsors?sponsorId=${id}`,
        method: 'PUT',
        body: data,
      }),
      providesTags: ['sponsors'],
    }),

    deleteSponsor: builder.mutation({
      query: ({ id }) => ({
        url: `/sponsors?sponsorId=${id}`,
        method: 'DELETE',
      }),
      providesTags: ['sponsors'],
    }),

    changeSponsorPassword: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/resetPassword?sponsorId=${id}`,
        method: 'POST',
        body: data,
      }),
      providesTags: ['sponsors'],
    }),

    addSponsorEvent: builder.mutation({
      query: ({ data }) => ({
        url: `/sponsors/events`,
        method: 'POST',
        body: data,
      }),
    }),

    updateSponsorEvent: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/events?eventId=${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    getSponsorEvents: builder.query({
      query: ({ id }) => ({
        url: `/sponsors/events?sponsorId=${id}`,
        method: 'GET',
      }),
    }),

    deleteSponsorEvent: builder.mutation({
      query: ({ id }) => ({
        url: `/sponsors/events?eventId=${id}`,
        method: 'DELETE',
      }),
    }),

    getCampaignByType: builder.mutation({
      query: () => ({
        url: `/Campaign/GetByCampaignType?campaignType=rag`,
        method: 'GET',
      }),
    }),

    getSmartReplyAgentsbyType: builder.query({
      query: () => ({
        url: `/SmartReply/GetBySmartReplyType?smartReplyType=whatsapp`,
        method: 'GET',
      }),
    }),
    getSmartReplyIntentMap: builder.query({
      query: ({ id }) => ({
        url: `/SmartReply/GetIntentMapping?smartReplyAgentId=${id}`,
        method: 'GET',
      }),
    }),

    getCampaignIntentMap: builder.mutation({
      query: ({ id }) => ({
        url: `/Campaign/GetIntentMapping?campaignId=${id}`,
        method: 'GET',
      }),
    }),

    createEventPackage: builder.mutation({
      query: ({ data }) => ({
        url: `/sponsors/packages`,
        method: 'POST',
        body: data,
      }),
    }),

    updateEventPackage: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/packages?packageId=${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // ----------------------- AB testing -----------------------
    getABTestGroups: builder.query({
      query: () => ({
        url: '/CampaignABTest/Group/list',
        method: 'GET',
      }),
      providesTags: ['abTest'],
    }),

    getABTestGroupById: builder.query({
      query: id => ({
        url: `/CampaignABTest/Group?groupId=${id}`,
        method: 'GET',
      }),
      providesTags: ['abTest'],
    }),

    createABTest: builder.mutation({
      query: body => ({
        url: '/CampaignABTest/Group',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['abTest'],
    }),

    toggleABTestStatus: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `/CampaignABTest/Group/ToggleStatus`,
        method: 'PUT',
        body: {
          groupId: id,
          isActive: isActive,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    addVariantToABTest: builder.mutation({
      query: ({ groupId, newCampaignId }) => ({
        url: `/CampaignABTest/Group/AddVariant`,
        method: 'POST',
        body: {
          groupId: groupId,
          newCampaignId: newCampaignId,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    deleteABTestGroup: builder.mutation({
      query: ({ groupId }) => ({
        url: '/CampaignABTest/Group',
        method: 'DELETE',
        body: {
          groupId,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    // ----------------------- Integrations -----------------------
    //* Webhooks

    getWebhooks: builder.query({
      query: () => ({
        url: '/integration?type=ctaWebhook',
        method: 'GET',
      }),
      providesTags: ['webhooks'],
    }),

    createWebhook: builder.mutation({
      query: body => ({
        url: '/integration',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['webhooks'],
    }),

    updateWebhook: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['webhooks'],
    }),

    deleteWebhook: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['webhooks'],
    }),

    getWebhookById: builder.query({
      query: ({ integrationId }) => ({
        url: `/integration/${integrationId}`,
        method: 'GET',
      }),
      providesTags: ['webhooks'],
    }),

    checkWebhookUpdate: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration/ctaWebhook/update-check?integrationId=${integrationId}`,
        method: 'POST',
        body,
      }),
    }),

    checkWebhookDelete: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/ctaWebhook/delete-check?integrationId=${integrationId}`,
        method: 'POST',
      }),
    }),

    testWebhook: builder.mutation({
      query: body => ({
        url: '/integration/ctaWebhook/test',
        method: 'POST',
        body,
      }),
    }),

    getWebhookLogs: builder.query({
      query: ({ integrationId, pageNumber = 1, limit = 10, sortOrder = 'desc' }) => ({
        url: `/WebhookLogs/FindAll?integrationId=${integrationId}&pageNumber=${pageNumber}&limit=${limit}&sortOrder=${sortOrder}`,
        method: 'GET',
      }),
      providesTags: ['webhookLogs'],
    }),

    // * Integration

    createIntegration: builder.mutation({
      query: ({ title }) => ({
        url: '/integration',
        method: 'POST',
        body: {
          title: title,
          integrationData: {
            type: 'whatsapp',
          },
        },
      }),
      invalidatesTags: ['Integrations'],
    }),

    startMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/meta/onboarding/start?integrationId=${integrationId}`,
        method: 'PUT',
        body: {},
      }),
    }),

    exchangeMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration/meta/onboarding/exchange?integrationId=${integrationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Integrations'],
    }),

    cancelMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/meta/onboarding/cancel?integrationId=${integrationId}`,
        method: 'PUT',
        body: {},
      }),
    }),

    getAvailableIntegrations: builder.query({
      query: () => ({
        url: '/integration/meta/available',
        method: 'GET',
      }),
      providesTags: ['Integrations'],
    }),

    getIntegrationsList: builder.query({
      query: () => ({
        url: '/integration?type=whatsapp',
        method: 'GET',
      }),
      providesTags: ['Integrations'],
    }),

    deleteIntegration: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Integrations'],
    }),
  }),
});

// Export hooks for each endpoint
export const {
  // User
  useSignUpMutation,
  useLoginMutation,
  useGetBrandingConfigQuery,
  useResendVerifyEmailCodeMutation,
  useVerifyUserEmailMutation,
  useForgotPasswordVerificationMutation,
  useForgotPasswordMutation,
  useGoogleAuthenticationQuery,
  useFacebookAuthenticationQuery,
  // ---------- Dashboard ------------
  useDashboardOverviewMutation,
  useRefreshDashboardMutation,
  // ------------------  Onboarding --------------
  useGetAllTasksQuery,
  useUpdateTaskMutation,
  useGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  // ------------------  User --------------
  useUserInfoQuery,
  useUpdateUserInfoMutation,
  useDeleteUserAccountMutation,
  useChangeUserPasswordMutation,
  // ---------- CTAs ------------
  useCreateCtaMutation,
  useEditCtaMutation,
  useDeleteCtaMutation,
  useDuplicateCtaMutation,
  useGetAllUserCtasQuery,
  useGetCtaByIdQuery,
  // ---------- Engagements ------------
  useCreateEngMutation,
  useEditEngMutation,
  useDeleteEngMutation,
  useDuplicateEngMutation,
  useGetAllUserEngQuery,
  useGetEngByIdQuery,
  // ---------- AI Engagement ------------
  useGetAllAIEngQuery,
  useDeleteAiEngagementMutation,
  useApproveAIEngMutation,
  useUnApproveAIEngMutation,
  useRegenerateAIEngagementMutation,
  useEditAIEngMutation,
  // ---------- End Screen ------------
  useCreateEndScreenMutation,
  useEditEndScreenMutation,
  useDeleteEndScreenMutation,
  useDuplicateEndScreenMutation,
  useGetAllUserEndScreenQuery,
  useGetEndScreenByIdQuery,
  //  -------------- Collections V1 ------------------
  useCreateCollectionV1Mutation,
  useGetAllUserCollectionsQuery,
  useDeleteCollectionV1Mutation,
  useGetWordpressPluginConnectionQuery,
  useGetAllSitemapsMutation,
  useRenameCollectionMutation,
  useRefreshSitemapMutation,
  useGetXmlPageCrawlerStatusQuery,
  useStartXMLCrawlingMutation,
  useAddPageManuallyWithFileMutation,
  useGetTargetPageListMutation,

  //  -------------- New Collection ------------------
  useCreateCollectionMutation,
  useListCollectionsQuery,
  useGetCollectionByIdQuery,
  useDeleteCollectionMutation,
  useAddCollectionItemsMutation,
  useAddCollectionEntryPointItemsMutation,
  useStartCollectionIndexingMutation,
  useDeleteCollectionItemMutation,
  useDeleteCollectionEntryPointMutation,
  useRefreshCollectionMutation,
  useGetCollectionItemsQuery,
  useGetCollectionsModuleOverviewQuery,
  useStartSitemapFetchMutation,
  useLazyGetSitemapFetchStatusQuery,
  useEditCollectionMutation,
  useEditCollectionItemMutation,
  useGetLabelsQuery,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useDeleteLabelMutation,
  useGetCollectionItemChunksQuery,
  useGetKnowledgeBaseChunkFilesQuery,
  useEditCollectionItemChunkMutation,
  useDeleteCollectionItemChunkMutation,

  //  -------------- Team ------------------
  useCreateTeamMutation,
  useGetTeamsQuery,
  useGetTeamByIdQuery,
  useInviteTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useGetPendingTeamInvitesQuery,
  useAcceptTeamInviteMutation,
  useRejectTeamInviteMutation,
  useGetTeamAccessableSectionsQuery,
  // ---------- Domains ------------
  useGetAllDomainsQuery,
  useCreateDomainMutation,
  useVerifyDomainMutation,
  useDeleteDomainMutation,
  useUpdatePageMetaDataMutation,
  // ---------- Pages ------------
  useGetAllPagesMutation,
  useGetSinglePageDataQuery,
  useExcludePageFromCampaignMutation,
  // ---------- Analytics ------------
  useConversionRateChartMutation,
  useEngagementRateChartMutation,
  useViewsChartMutation,
  useTimeSpentMutation,
  useTopActionCardsMutation,
  useTopEngagementCardsMutation,
  useTopEndScreenCardsMutation,
  useTimeLineChartMutation,
  usePerformanceChartMutation,
  useCountriesChartMutation,
  useCitiesChartMutation,
  useBrowsersChartMutation,
  useDevicesChartMutation,
  // ---------- Agents ------------
  useUserConfigurationQuery,
  useAllAgentsQuery,
  useGetAgentByIdQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useEditAgentMutation,
  useDuplicateAgentMutation,
  useEditAgentStateMutation,
  useCreateScreenFromTemplateMutation,
  useRefreshAgentOverviewMutation,
  useGetAgentOverviewCacheStatusMutation,
  useAgentOverviewSummaryMutation,
  useDownloadPersonalizationToolRegistrationLeadsMutation,
  useAgentActivityMutation,
  useAgentOverviewTimelineMutation,

  useUploadFontMutation,
  useGetAllFontHistoryQuery,
  useAgentOverviewTopEngagementsMutation,
  useAgentOverviewTopEngagementsPaginationMutation,
  useAgentOverviewTopEngagementsDownloadMutation,
  useWhatsAppOverviewTopEngagementsMutation,
  useGetKAReferenceOverviewMutation,
  useAgentOverviewTopActionsMutation,
  useAgentIRISSessionsMutation,
  useAgentOverviewWordCloudMutation,
  useAgentOverviewWordCloudDownloadMutation,
  useAgentOverviewFeedbackMutation,
  useDownloadAgentOverviewFeedbackMutation,
  useAgentOverviewPagesMutation,
  useAgentOverviewPagesDownloadMutation,
  useAgentOverviewTopVisitorsQuery,
  useAgentOverviewTopVisitorsDynamicFormMutation,
  useAgentOverviewTopVisitorsJourneyCtasQuery,
  useAgentOverviewTopVisitorsDownloadMutation,
  useAIGeneratedExamplesMutation,
  useGetDomainConfigHistoryQuery,
  useClearFAQCacheMutation,
  useDomainBrandSyncQuery,
  useGetAIBrandSyncSuggestionsMutation,
  // ---------------------- AI Agent ----------------------
  useAllAIAgentsQuery,
  useGetAIAgentByIdQuery,
  useCreateAIAgentMutation,
  useUpdateAIAgentMutation,
  useDeleteAIAgentMutation,
  // ---------------------- Smart Reply ----------------------
  useGetAllSmartRepliesQuery,
  useGetSmartReplyByIdQuery,
  useCreateSmartReplyMutation,
  useUpdateSmartReplyMutation,
  useDeleteSmartReplyMutation,
  // ---------------------- Smart Reply Overview ----------------------
  useGetSmartReplyOverviewQuery,
  useGetSmartReplyOverviewTimelineQuery,
  useGetSmartReplyEmailLogsQuery,
  useGetSmartReplyEmailThreadQuery,
  useGetSmartReplyTemplatesQuery,
  useGetLedgerHistoryQuery,
  useGetSmartReplyBroadcastListQuery,
  useDeleteSmartReplyBroadcastMutation,
  // ---------------------- Personalization Email Channel ----------------------
  useGetPtTargetListQuery,
  useGetPtTargetListMetaDataFieldsQuery,
  useImportProfilesFromCsvMutation,
  useGetCsvImportColumnsQuery,
  useImportProfilesFromHubspotMutation,
  useGetProfileImportStatusQuery,
  useGetPersonalizationProfileListQuery,
  useGetCrmIntegrationsListQuery,
  useGetIntegrationHubPropertiesQuery,
  useCreateHubspotPropertyMutation,
  useConnectEmailChannelHubspotMutation,
  useDeleteCrmIntegrationMutation,
  useVerifyHubspotIntegrationMutation,
  useGetPersonalizationAgentsQuery,
  useCreatePersonalizationAgentMutation,
  usePreviewPersonalizationAgentMutation,
  useStartPersonalizationAgentMutation,
  useGetPersonalizationAgentQuery,
  useDeletePersonalizationAgentMutation,
  useGetPersonalizationAgentProfilesQuery,
  useGetPersonalizationAgentProfileQuery,
  useRetryPersonalizationAgentFailedProfilesMutation,
  useDownloadPersonalizationAgentExportMutation,
  usePublishPersonalizationAgentIntegrationMutation,
  useGetPersonalizationAgentPublishStatusQuery,
  useGetPersonalizationAgentDeliveriesQuery,
  useGetPTInputLogsQuery,
  useGetPersonalizationByIdQuery,
  useGetPTTopChunksQuery,
  useGetPTFunnelOverviewQuery,
  useGetPTRecommendationTypesQuery,
  // ---------------------- Qualification ----------------------
  useGetAllQualificationAgentsQuery,
  useCreateQualificationMutation,
  useGetQualificationByIdQuery,
  useUpdateQualificationMutation,
  useDeleteQualificationMutation,

  // ---------------------- Qualification Agent v1 ----------------------
  useCreateQualificationAgentV1Mutation,
  useUpdateQualificationAgentV1Mutation,
  useGetQualificationAgentV1ByIdQuery,
  useGetAllQualificationAgentsV1Query,
  useDeleteQualificationAgentV1Mutation,
  // ---------------------- Qualification Overview ----------------------
  useGetQualificationOverviewQuery,
  useGetQualificationAudienceQuery,
  useGetQualificationStageBreakdownQuery,
  useGetQualificationSignalsBreakdownQuery,
  useGetQualificationVisitorsBreakdownQuery,
  useSubmitSmartReplyFeedbackMutation,
  // ---------------------- Folders ----------------------
  useGetAllFoldersQuery,
  useGetFolderByIdQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useAddCampaignToFolderMutation,
  useRemoveCampaignFromFolderMutation,
  // ---------------------- Action Bucket ----------------------
  useGetAllActionBucketQuery,
  useGetCollectionOverviewQuery,
  useUpdateAuthInfoMutation,
  useRefreshCtaFromCollectionMutation,
  useDeleteCtaFromCollectionMutation,
  useUpdateUseAsReferenceMutation,
  useDeleteMediaFromCollectionMutation,
  useUpdateMediaUseAsReferenceMutation,
  useAddPageToCollectionMutation,
  useAddMediaToCollectionMutation,
  useGetWordpressFiltersQuery,
  useUpdateMediaPreferredLinkMutation,
  // ----------------------- Leads -----------------------
  useGetAllLeadsListMutation,
  // useDownloadLeadsListMutation,
  // ----------------------- Profiles -----------------------
  useGetProfilesMutation,
  useGetTargetListMutation,
  useGetTargetListMetaDataFieldsQuery,
  useDownloadTargetListMutation,
  useUploadTargetListCsvMutation,
  useImportTargetListMutation,
  useGetTargetListImportStatusQuery,
  useGetTargetListImportDetailsQuery,
  useDownloadProfilesMutation,
  useGetProfilesMetaDataFieldsQuery,
  // ----------------------- Draft ---------------------
  useGetAllDraftsQuery,
  useGetSingleDraftQuery,
  useCreateDraftMutation,
  useDeleteDraftMutation,
  // ----------------------- AI Lab -----------------------
  useGetAllEnginesQuery,
  useGetDefaultEngineQuery,
  useGetEngineByIdQuery,
  useDeleteEngineMutation,
  useCreateEngineMutation,
  useUpdateEngineMutation,
  useSimulateEngineMutation,
  useSimulateFAQCreationsMutation,
  useGetLatestSimulateMutation,
  useStartSimulationFileMutation,
  useGetSimulateStatusMutation,
  useGetSimulationResultMutation,
  useStartLlmEvalMutation,
  useGetLlmEvalStatusMutation,
  useGetLlmEvalResultMutation,
  useUploadSimulateEngineResultMutation,
  useGenerateGuidelineMutation,
  useCheckSpamGuidelineMutation,
  useGetEngineHistoryQuery,
  useSaveEngineVersionMutation,
  useRollbackEngineVersionMutation,
  useDeleteEngineVersionMutation,
  useEditEngineVersionMutation,
  useDuplicateEngineMutation,
  useBulkSimulationHistoryQuery,
  useLazyBulkSimulationHistoryQuery,
  useUpdateAndSelectEngineMutation,
  useGetNoDefaultEngineQuery,
  // ----------------------- Fine Tuning -----------------------
  useCreateFineTuningJobMutation,
  useGetFineTuningJobsQuery,
  useGetEngineFeedbackPaginationWithFilterMutation,
  useGetFeedbackCountByTypeMutation,
  useGetFeedbackCountByTypeTableMutation,
  // ----------------------- Etc -----------------------
  useUploadImageMutation,
  useDeleteImageMutation,
  useFeedbackMutation,
  useSupportMutation,
  useUploadDocumentMutation,
  useGetTeamBrandingConfigQuery,

  // Credit Usage
  useGetCreditUsageQuery,
  useGetCreditUsageTimelineMutation,
  useGetAgentSummaryMutation,
  useGetPlaybooksQuery,
  useCreateBridgedEventMutation,
  useGetBridgedEventsQuery,
  useGetBridgedEventByIdQuery,
  useUpdateBridgedEventMutation,
  useDeleteBridgedEventMutation,
  useGetCreditUsageCustomWorksMutation,
  useRefreshCreditUsageMutation,
  useSetSmartReplyBroadcastMutation,

  //Sponsors
  useGetSponsorsListQuery,
  useCreateSponsorMutation,
  useUpdateSponsorMutation,
  useDeleteSponsorMutation,
  useChangeSponsorPasswordMutation,
  useAddSponsorEventMutation,
  useUpdateSponsorEventMutation,
  useGetSponsorByIdQuery,
  useGetSponsorEventsQuery,
  useDeleteSponsorEventMutation,
  useGetCampaignByTypeMutation,
  useGetSmartReplyAgentsbyTypeQuery,
  useGetSmartReplyIntentMapQuery,
  useLazyGetSmartReplyIntentMapQuery,
  useGetCampaignIntentMapMutation,
  useCreateEventPackageMutation,
  useUpdateEventPackageMutation,

  // ----------------------- AB testing -----------------------
  useGetABTestGroupsQuery,
  useGetABTestGroupByIdQuery,
  useCreateABTestMutation,
  useToggleABTestStatusMutation,
  useAddVariantToABTestMutation,
  useDeleteABTestGroupMutation,

  // ----------------------- Integrations -----------------------
  //* Webhooks
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useLazyGetWebhookByIdQuery,
  useGetWebhookByIdQuery,
  useCheckWebhookUpdateMutation,
  useCheckWebhookDeleteMutation,
  useTestWebhookMutation,
  useGetWebhookLogsQuery,

  // * Whatsapp integration
  useCreateIntegrationMutation,
  useStartMetaWhatsappOnboardingMutation,
  useExchangeMetaWhatsappOnboardingMutation,
  useCancelMetaWhatsappOnboardingMutation,
  useGetAvailableIntegrationsQuery,
  useGetIntegrationsListQuery,
  useDeleteIntegrationMutation,
} = bridgedApi;
